import React, { ChangeEvent, FC, useCallback, useMemo, useRef, useState } from 'react'
import { Currency, SupportedLocale } from '@afterpay/types'
import { Button, Checkbox, Heading, Icon, Input, Modal, Text, useMoney } from '@afterpaytouch/core'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import { MAX_FILE_SIZE } from '../../../../../model'
import { useConsumerSizes } from '../../../../../utils/responsive'
import { checkAmount } from '../../../utils'
import { getSelectedItemDisputeAmount } from '../../hooks'
import { Section75DisputeModalSteps, SelectedItem, useSection75DisputeModalContext } from '../../Section75DisputeModalContext'

const I18N_NAMESPACE = ['dispute']

export const isValidCustomAmountValue = (amount: string): boolean => {
  const trimmedAmount = amount.trim()

  if (!/^\d+(\.\d{1,2})?$/.test(trimmedAmount)) {
    return false
  }

  const numericAmount = Number(trimmedAmount)
  return Number.isFinite(numericAmount) && numericAmount > 0 && checkAmount(numericAmount)
}

export const normalizeCustomAmountValue = (amount: string): string => Number(amount.trim()).toFixed(2)

export const DisputeAmount: FC = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { textSize } = useConsumerSizes()
  const router = useRouter()
  const locale = router?.locale as SupportedLocale
  const { setStep, order, selectedItems, setSelectedItems, currentItemIndex, setCurrentItemIndex, files, setFiles } = useSection75DisputeModalContext()

  const [error, setError] = useState<string>('')
  const uploadRef = useRef<HTMLInputElement>(null)
  const currentItem = currentItemIndex !== null ? selectedItems[currentItemIndex] : undefined

  const { formatMoney } = useMoney({
    currency: (currentItem?.amount?.currency ?? order?.orderSummary?.orderTotal?.currency ?? 'GBP') as Currency,
    locale,
  })

  const updateCurrentItem = useCallback(
    (updates: (item: SelectedItem) => Partial<SelectedItem>) => {
      if (currentItemIndex === null) {
        return
      }

      setSelectedItems((prev) => {
        const current = prev[currentItemIndex]
        if (current === undefined) return prev

        const updated = [...prev]
        updated[currentItemIndex] = {
          ...current,
          ...updates(current),
        }
        return updated
      })
    },
    [currentItemIndex, setSelectedItems]
  )

  const customAmountValue = currentItem?.disputeAmount?.amount ?? currentItem?.amount?.amount ?? ''
  const customAmountInvalid = currentItem?.useCustomAmount === true && !isValidCustomAmountValue(customAmountValue)

  const disputeAmount = useMemo(() => {
    return formatMoney(currentItem !== undefined ? getSelectedItemDisputeAmount(currentItem) : '0.00')
  }, [currentItem, formatMoney])

  const backHandler = useCallback(() => {
    setStep(Section75DisputeModalSteps.DisputeDetails)
  }, [setStep])

  const handleNext = useCallback(() => {
    if (customAmountInvalid) {
      setError(t('dispute:section75DisputeModal:disputeAmount:amountError'))
      return
    }

    if (currentItem?.useCustomAmount === true) {
      updateCurrentItem((item) => ({
        disputeAmount: {
          ...item.amount,
          amount: normalizeCustomAmountValue(customAmountValue),
        },
      }))
    }

    setError('')

    if (currentItemIndex !== null && currentItemIndex < selectedItems.length - 1) {
      setCurrentItemIndex(currentItemIndex + 1)
      setStep(Section75DisputeModalSteps.DisputeDetails)
      return
    }

    setCurrentItemIndex(null)
    setStep(Section75DisputeModalSteps.OtherDocs)
  }, [
    currentItem?.useCustomAmount,
    currentItemIndex,
    customAmountInvalid,
    customAmountValue,
    selectedItems.length,
    setCurrentItemIndex,
    setStep,
    t,
    updateCurrentItem,
  ])

  const handleCustomAmountChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked

      updateCurrentItem((item) => ({
        useCustomAmount: checked,
        disputeAmount: checked
          ? {
              ...item.amount,
              amount: item.disputeAmount?.amount ?? item.amount?.amount ?? '0',
            }
          : undefined,
        amountReason: checked ? item.amountReason ?? '' : '',
      }))

      setError('')
    },
    [updateCurrentItem]
  )

  const handleCustomAmountValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const amount = event.target.value.trim()

      updateCurrentItem((item) => ({
        disputeAmount: {
          ...item.amount,
          amount,
        },
      }))

      setError('')
    },
    [updateCurrentItem]
  )

  const handleAmountReasonChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const amountReason = event.target.value

      updateCurrentItem(() => ({
        amountReason,
      }))
    },
    [updateCurrentItem]
  )

  const addFileButtonClickHandler = useCallback(() => {
    uploadRef.current?.click()
  }, [])

  const fileChangedHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const fileList = Array.from(event.target.files ?? [])

      const fileOutOfSizeList = fileList.filter((file: File) => file.size > MAX_FILE_SIZE)
      if (fileOutOfSizeList.length > 0) {
        setError(t('dispute:submitEvidence:error:exceedSize'))
        return
      }

      setFiles([...files, ...fileList])

      const newFileNames = fileList.map((file) => file.name)
      updateCurrentItem((item) => ({
        fileKeys: [...(item.fileKeys ?? []), ...newFileNames],
      }))

      setError('')
      event.target.value = ''
    },
    [files, setFiles, t, updateCurrentItem]
  )

  const uploadedFiles = currentItem?.fileKeys ?? []

  return (
    <>
      <Modal.Header divider={true}>
        <div className='absolute z-10 pt-1'>
          <Button.Link size='Small' onClick={backHandler}>
            <Icon.CaretLeft size='Small' />
          </Button.Link>
        </div>
        <div className='text-center'>
          <Heading size='L'>{t('dispute:section75DisputeModal:disputeAmount:heading')}</Heading>
          {selectedItems.length > 1 && currentItemIndex !== null && (
            <Text size='S' className='mt-1 text-gray-600'>
              {t('dispute:section75DisputeModal:disputeDetails:itemCount', { current: currentItemIndex + 1, total: selectedItems.length })}
            </Text>
          )}
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className='space-y-6'>
          {currentItem !== undefined && (
            <div>
              <Heading size='L'>{currentItem.name}</Heading>
            </div>
          )}

          <div>
            <Text size={textSize} className='mb-4'>
              {t('dispute:section75DisputeModal:disputeAmount:amount')}
            </Text>
            <Text size='XXXXL' bold className='mb-4'>
              {disputeAmount}
            </Text>
          </div>

          <div>
            <Checkbox
              id={`custom-amount-${currentItem?.key ?? currentItemIndex ?? 'none'}`}
              name='custom-amount'
              onChange={handleCustomAmountChange}
              label={t('dispute:section75DisputeModal:disputeAmount:notSameAmount')}
              checked={Boolean(currentItem?.useCustomAmount)}
            />
          </div>

          {currentItem?.useCustomAmount === true && (
            <div>
              <Text size={textSize} className='mb-4'>
                {t('dispute:section75DisputeModal:disputeAmount:customAmountQuestion')}
              </Text>
              <div className='mb-4 flex items-stretch'>
                <Input.Number
                  label={t('dispute:section75DisputeModal:disputeAmount:customAmountLabel')}
                  icon='Coins'
                  iconPosition='Left'
                  fullWidth={true}
                  value={customAmountValue}
                  onChange={handleCustomAmountValueChange}
                  placeholder='£0.00'
                />
                <div className='p-md flex items-center justify-center'>
                  <Text>{currentItem.amount.currency ?? order?.orderSummary?.orderTotal?.currency}</Text>
                </div>
              </div>
              <Input.TextArea
                rows={4}
                fullWidth={true}
                label={t('dispute:section75DisputeModal:disputeAmount:reasonLabel')}
                value={currentItem.amountReason ?? ''}
                onChange={handleAmountReasonChange}
              />
            </div>
          )}

          {currentItem?.useCustomAmount === true && (
            <div>
              <Text size={textSize} bold>
                {t('dispute:section75DisputeModal:disputeAmount:supportingDocs')}
              </Text>
              <Text className='mt-1 text-gray-600'>{t('dispute:section75DisputeModal:disputeAmount:supportingDocsDesc')}</Text>

              <div className='mt-3'>
                <Button.Ghost size='Regular' padding='Fluid' onClick={addFileButtonClickHandler}>
                  {t('dispute:section75DisputeModal:disputeAmount:chooseFiles')}
                </Button.Ghost>
                <input type='file' className='hidden' multiple onChange={fileChangedHandler} accept='image/*,.pdf,.doc,.docx' ref={uploadRef} />
              </div>

              {uploadedFiles.length > 0 && (
                <div className='mt-3'>
                  <Text className='text-gray-600'>{t('dispute:section75DisputeModal:disputeAmount:uploadedFiles')}</Text>
                  <ul className='mt-1'>
                    {uploadedFiles.map((fileName) => (
                      <li key={fileName} className='text-sm text-gray-700'>
                        <Text className='text-gray-600'>{fileName}</Text>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {error !== '' && (
            <div>
              <Text size={textSize} color='Fire'>
                {error}
              </Text>
            </div>
          )}
        </div>

        <div className='mt-6'>
          <Button padding='Fluid' size='Large' onClick={handleNext}>
            {t(
              `dispute:section75DisputeModal:disputeAmount:${currentItemIndex !== null && currentItemIndex < selectedItems.length - 1 ? 'next_item' : 'next'}`
            )}
          </Button>
        </div>
      </Modal.Content>
    </>
  )
}
